import { ReassignTypeEnum } from '@common/types';
import { ToastType } from '@components';
import { softDeleteFlagTypes } from '@components/FlagPopup/constants';
import { SpecialFlagTypeEnum } from '@components/FlagPopup/types';
import { FeatureFlags } from '@constants/featureFlags';
import { fetchCarrierOwner } from '@store/capacityTools/services';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LTLReferenceNumberTypes } from '@store/ltl/types';
import { closeModal } from '@store/modals';
import {
  addFlag,
  Carrier,
  CarrierState,
  carrierStore,
  deleteFlag,
  fetchUserInfoUserModel,
  FlagType,
  LTLReferenceNumberCollection,
  orderStore,
  putFalloutCount,
  setCarrierStore,
  setOrderStore,
  updateFlag,
  updateLoadPropertyAtIndex,
  updateOrderState,
  UserInfoModel,
} from '@store/orders';
import { bounceCarrier } from '@store/orders/services';
import { BounceCarrierPayload, CarrierFeatures } from '@store/orders/types';
import { userStore } from '@store/user';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { handleToast, printError } from '@utils/utils';
import { isEmpty } from 'lodash';
import { produce } from 'solid-js/store';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import PointyCheck from '../../../../../../assets/PointyCheck.svg';
import PointyX from '../../../../../../assets/PointyX.svg';
import { loadInfoStore, setLoadInfoStore } from './store';

const setAutoCoveredBy = (
  loadIndex: number,
  coveredById: number,
  coveredBy: string,
  message: string,
) => {
  const currentCoveredById = orderStore.order.loads[loadIndex]?.coveredById;
  updateLoadPropertyAtIndex(
    {
      coveredById,
      coveredBy,
    },
    loadIndex,
  );
  if (currentCoveredById === coveredById) {
    return;
  }
  handleToast(ToastType.Info, message);
};

export const applyOwnedCarrierRep = async (carrierId: number, idx?: number) => {
  const loadIndex = idx ?? orderStore.activeTab.index;

  if (!isFeatureFlagEnabled(FeatureFlags.AutoPopulateOwnedCapacityRep)()) {
    return false;
  }

  const owner = await fetchCarrierOwner(carrierId);
  if (!owner) {
    return false;
  }

  setAutoCoveredBy(
    loadIndex,
    owner.id,
    owner.name ?? '',
    `Owned carrier - Covered By set to ${owner.name ?? 'its rep'}`,
  );

  return true;
};

const ATG_COSTORE = 'ATG CoStore - Brokerage';

const normalizeOfficeName = (officeName: string | null | undefined) =>
  (officeName ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s*[\u2010-\u2015\u2212-]\s*/g, '-');

const isAtgCoStoreBrokerageOffice = (officeName: string | null | undefined) =>
  normalizeOfficeName(officeName) === normalizeOfficeName(ATG_COSTORE);

const hasCarrierOwner = (
  carrierOwnerUserId: number | null | undefined,
): carrierOwnerUserId is number =>
  typeof carrierOwnerUserId === 'number' && carrierOwnerUserId > 0;

export const isOwnedCapacityCoveredByEnabled = (
  mode: string | null | undefined,
) =>
  (mode === 'TL' || mode === 'Partial') &&
  isFeatureFlagEnabled(FeatureFlags.AutoPopulateOwnedCapacityRep)();

export const applyCoStoreManualCoveredBy = (
  carrier:
    | Pick<Carrier, 'carrierOwnerUserId' | 'carrierOwnerUserName'>
    | undefined,
  loadIndex: number,
) => {
  const ownerId = carrier?.carrierOwnerUserId;
  if (
    isAtgCoStoreBrokerageOffice(userStore.user.office) &&
    hasCarrierOwner(ownerId)
  ) {
    setAutoCoveredBy(
      loadIndex,
      ownerId,
      carrier?.carrierOwnerUserName ?? '',
      `Owned carrier - Covered By set to ${carrier?.carrierOwnerUserName ?? 'its rep'}`,
    );
    return;
  }

  setAutoCoveredBy(
    loadIndex,
    userStore.user.id,
    userStore.user.name,
    `Covered By set to ${userStore.user.name}`,
  );
};

// Offer-manager booking. Shared capacity on uses the carrier owner for any
// office. Shared capacity off uses the owner only for ATG CoStore - Brokerage.
// Otherwise Covered By is the rep who submitted the bid.
export const applyOfferManagerCoveredBy = (
  carrier: Pick<Carrier, 'carrierOwnerUserId' | 'carrierOwnerUserName'>,
  bid: { userId: number; requestedBy: string } | undefined,
  sharedCapacity: boolean,
  loadIndex: number,
) => {
  const ownerId = carrier.carrierOwnerUserId;
  const assignOwner =
    hasCarrierOwner(ownerId) &&
    (sharedCapacity || isAtgCoStoreBrokerageOffice(userStore.user.office));

  if (assignOwner) {
    setAutoCoveredBy(
      loadIndex,
      ownerId,
      carrier.carrierOwnerUserName ?? '',
      `Owned carrier - Covered By set to ${carrier.carrierOwnerUserName ?? 'its rep'}`,
    );
    return;
  }

  if (!bid || bid.userId <= 0) {
    return;
  }

  setAutoCoveredBy(
    loadIndex,
    bid.userId,
    bid.requestedBy,
    `Covered By set to ${bid.requestedBy} (submitted the bid)`,
  );
};

export const handleReassignedUser = async (id: number, type: string) => {
  try {
    setLoadInfoStore('isLoading', true);
    const response: UserInfoModel = await fetchUserInfoUserModel(id);

    if (type === ReassignTypeEnum.CoveredBy) {
      setLoadInfoStore('coveredBy', response);
      updateLoadPropertyAtIndex({
        coveredBy: response.name,
        coveredById: response.id,
      });
    } else if (type === ReassignTypeEnum.AssignedTo) {
      setLoadInfoStore('assignedTo', response);
      updateLoadPropertyAtIndex({
        assignedToUser: response.name,
        assignedToUserId: response.id,
      });
    } else if (type === ReassignTypeEnum.CapacityOpsRep) {
      setLoadInfoStore('capacityOpsRepUser', response);
      updateLoadPropertyAtIndex({
        capacityOpsRepUser: response.name,
        capacityOpsRepUserId: response.id,
      });
    }
  } catch (err) {
    handleToast(ToastType.Error, 'Failed to reassign user');
  } finally {
    setLoadInfoStore('isLoading', false);
  }
};

// Removes the carrier from the load. Changes are saved when the user saves the order.
const removeCarrierFromLoad = (tabIndex: number, load?: ILoadEditViewModel) => {
  setCarrierStore(
    produce((s: CarrierState) => {
      s.carrier[tabIndex] = {} as Carrier;
      s.featureFlags[tabIndex] = {} as CarrierFeatures;
    }),
  );
  const stops = load?.stops?.map((stop) => ({
    ...stop,
    driverInTime: null,
    driverOutTime: null,
    //remove the conditional edi updates in this condition
    shouldSendEdiBooleanVals: {
      shouldSendPickupAppointmentAuto214: false,
      shouldSendDropoffAppointmentAuto214: false,
      shouldSendDropoffDriverInAuto214: false,
      shouldSendPickupDriverInAuto214: false,
      shouldSendPickupDriverOutAuto214: false,
      shouldSendDropoffDriverOutAuto214: false,
    },
    ediUpdates: [],
  }));
  if (stops) {
    updateLoadPropertyAtIndex({ stops: stops }, tabIndex);
  }
  updateLoadPropertyAtIndex(
    {
      driver: undefined,
      driverPhoneNumber: undefined,
      truckNumber: undefined,
      trailerNumber: undefined,
      carrierId: undefined,
      carrierName: undefined,
      carrierContactId: undefined,
      status: 'Unassigned',
    },
    tabIndex,
  );
};

export const incrementFallout = async (
  carrierId: number,
  tabIndex: number,
  load?: ILoadEditViewModel,
) => {
  await putFalloutCount(
    carrierId,
    CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsFallout,
  );
  removeCarrierFromLoad(tabIndex, load);
};

export const bounceCarrierFromLoad = async (
  payload: BounceCarrierPayload,
  tabIndex: number,
  bouncedCarrierId: number,
) => {
  await bounceCarrier(
    payload,
    CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsBounceCarrier,
  );

  const currentLoad = orderStore.order.loads.at(tabIndex);
  if (
    currentLoad?.id !== payload.loadId ||
    currentLoad.carrierId !== bouncedCarrierId
  ) {
    return;
  }
  removeCarrierFromLoad(tabIndex, currentLoad);
};

// A carrier saved on the load can only be removed through Bounce Carrier.
export const hasSavedCarrier = (load: ILoadEditViewModel) =>
  load.id > 0 && Boolean(load.carrierId) && !load.isCarrierDirty;

export const getCarrierContact = (
  load: ILoadEditViewModel,
  tabIndex: number,
) => {
  if (!isEmpty(carrierStore.carrier[tabIndex])) {
    return carrierStore.carrier[tabIndex]?.contacts?.find(
      (v) => v.id === load.carrierContactId,
    );
  }
};

export const getContactPhoneVerificationIcon = (
  load: ILoadEditViewModel,
  tabIndex: number,
) => {
  const carrierContact = getCarrierContact(load, tabIndex);

  if (
    carrierContact === undefined ||
    (carrierContact.phone1 || '').trim() === ''
  )
    return '';

  if (carrierContact.ownershipSource === 1) {
    return PointyCheck;
  }

  return PointyX;
};

export const getContactEmailVerificationIcon = (
  load: ILoadEditViewModel,
  tabIndex: number,
) => {
  const carrierContact = getCarrierContact(load, tabIndex);

  if (
    carrierContact === undefined ||
    carrierContact.email == undefined ||
    carrierContact.email.trim() === ''
  )
    return '';

  if (carrierContact.ownershipSource === 1) {
    return PointyCheck;
  }

  return PointyX;
};

export const getContactLevelVerificationIcon = (
  load: ILoadEditViewModel,
  tabIndex: number,
) => {
  const carrierContact = getCarrierContact(load, tabIndex);
  if (carrierContact === undefined) return '';

  if (carrierContact.ownershipSource === 1) {
    return PointyCheck;
  }

  return PointyX;
};

export const getContactLevelVerificationTooltip = (
  load: ILoadEditViewModel,
  tabIndex: number,
) => {
  const carrierContact = getCarrierContact(load, tabIndex);
  if (carrierContact === undefined) return '';

  if (carrierContact.ownershipSource === 1) {
    return 'Highway Verified Contact';
  }

  return 'Unverified Contact: Manually added to ATG and not verified';
};

export const canOverrideCarrierHistory = () => {
  if (
    userStore.user.officeSettings &&
    userStore.user.officeSettings.length > 0
  ) {
    const c = userStore.user.officeSettings.find((obj) => {
      return obj.key == 'AllowCarrierHistoryOverride';
    });
    if (c && c.value) return c.value === 'true';
  }
  return false;
};

export const getTotalWeightAndUnit = (
  load: ILoadEditViewModel,
  cb: (weightString: string) => void,
) => {
  if (load.items && load.items.length > 0) {
    const weightUnit = load.items[0].uom || 'lbs';
    const totalWeight = load.items.reduce((total, item) => {
      return item.operationType !== 'Delete'
        ? Number(total) + Number(item.weight)
        : Number(total);
    }, 0);
    const weightString = `${totalWeight.toLocaleString()} ${
      weightUnit === 'kg' ? 'kgs' : weightUnit
    }.`;
    cb(weightString);
    return weightString;
  }
  return `${0} lbs.`;
};

export function updateReferenceNumber(
  type: LTLReferenceNumberTypes,
  value: string,
  referenceNumbers: LTLReferenceNumberCollection | undefined,
): LTLReferenceNumberCollection {
  const updatedReferenceNumbers: LTLReferenceNumberCollection = {
    operationType: 'Update',
    referenceNumbers: referenceNumbers
      ? [...referenceNumbers.referenceNumbers]
      : [],
  };

  updatedReferenceNumbers.referenceNumbers =
    updatedReferenceNumbers.referenceNumbers.some(
      (referenceNumber) => referenceNumber.type === type,
    )
      ? updatedReferenceNumbers.referenceNumbers.map((refNumber) =>
          refNumber.type === type ? { ...refNumber, type, value } : refNumber,
        )
      : [...updatedReferenceNumbers.referenceNumbers];

  return updatedReferenceNumbers;
}

export const onFlagChange = async (
  flag: FlagViewModel,
  action: 'update' | 'create' | 'delete',
  load: ILoadEditViewModel,
  tabIndex: number,
  editFlagModalId: string,
) => {
  if (flag.description != null && flag.description.trim() === '') {
    flag.description = undefined;
  }
  setLoadInfoStore('isLoading', true);
  closeModal(editFlagModalId);

  const loads = [...orderStore.order.loads];
  // we just need these printlogs to make stuff happy
  const flagsForLoad = [...(loads[tabIndex].flags ?? [])];
  const currentIndex = loadInfoStore.flagIndex;

  try {
    switch (action) {
      case 'delete': {
        if (currentIndex === null) {
          return;
        }

        const response = await deleteFlag(
          flag.id as number,
          undefined,
          CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsFlagDelete,
        );

        if (response) {
          loads[tabIndex] = { ...loads[tabIndex] };

          if (
            softDeleteFlagTypes[
              load.flags?.[currentIndex]
                .flagType as unknown as SpecialFlagTypeEnum
            ]
          ) {
            flagsForLoad[currentIndex] = {
              ...flagsForLoad[currentIndex],
              cleared: true,
            };
          } else {
            flagsForLoad.splice(currentIndex, 1);
          }

          loads[tabIndex].flags = flagsForLoad;

          updateOrderState({
            ...orderStore.order,
            loads: loads,
          });
          handleToast(ToastType.Success, 'The Flag was cleared.');
        }

        setLoadInfoStore('flagIndex', null);
        setLoadInfoStore('flag', undefined);
        break;
      }
      case 'update': {
        if (currentIndex === null) {
          return;
        }
        const response = await updateFlag(
          flag,
          CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsFlagUpdate,
        );
        loads[tabIndex] = { ...loads[tabIndex] };

        if (loads[tabIndex].flags !== undefined) {
          flagsForLoad[currentIndex] = response;
          loads[tabIndex].flags = flagsForLoad;
        }

        setOrderStore(
          'order',
          'loads',
          orderStore.activeTab.index,
          'flags',
          flagsForLoad,
        );

        setLoadInfoStore('flagIndex', null);
        closeModal(editFlagModalId);
        setLoadInfoStore('flag', undefined);

        break;
      }
      case 'create': {
        flag.orderId = orderStore.order.id;
        flag.loadId = load.id;
        flag.setBy = userStore.user.name;

        const response = await addFlag(
          flag,
          undefined,
          CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsFlagCreate,
        );
        flagsForLoad.push(response);
        loads[tabIndex] = {
          ...loads[tabIndex],
          flags: flagsForLoad,
        };

        setOrderStore(
          'order',
          'loads',
          orderStore.activeTab.index,
          'flags',
          flagsForLoad,
        );

        if (response.flagType === FlagType.NeedsApproval) {
          updateOrderState({
            needsApprovalFlag: { ...response },
          });
        }

        closeModal(editFlagModalId);
        setLoadInfoStore('flag', undefined);

        break;
      }
    }
  } catch (error) {
    printError(error);
    handleToast(ToastType.Error, `Failed to ${action} flag`);
  }

  setLoadInfoStore('isLoading', false);
};
